import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  FileText, Plus, Eye, XCircle, Download, Loader2,
  Building2, Hash, DollarSign, CheckCircle, Clock,
  AlertCircle, ChevronDown, ArrowLeft, Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  registered: { label: "Registrada", bg: "bg-emerald-500/10", text: "text-emerald-400", icon: CheckCircle },
  draft: { label: "Borrador", bg: "bg-blue-500/10", text: "text-blue-400", icon: Clock },
  cancelled: { label: "Anulada", bg: "bg-red-500/10", text: "text-red-400", icon: XCircle },
  sent: { label: "Enviada", bg: "bg-yellow-500/10", text: "text-yellow-400", icon: Clock },
  rejected: { label: "Rechazada", bg: "bg-red-500/10", text: "text-red-400", icon: AlertCircle },
};

// ─── INVOICE LIST ───
const InvoiceList = ({ invoices, onView, onCancel, loading }) => {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#f59e0b] animate-spin" /></div>;

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12 bg-[#0a0f1a] border border-white/5">
        <FileText className="w-12 h-12 text-[#f59e0b]/20 mx-auto mb-3" />
        <p className="text-[#94a3b8]">No hay facturas emitidas aun</p>
        <p className="text-[#94a3b8] text-xs mt-1">Crea tu primera factura electronica</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {invoices.map((inv, i) => {
        const st = STATUS_COLORS[inv.status] || STATUS_COLORS.draft;
        const StIcon = st.icon;
        return (
          <motion.div
            key={inv.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="bg-[#0a0f1a] border border-white/5 p-4 hover:border-[#f59e0b]/20 transition-colors cursor-pointer"
            onClick={() => onView(inv)}
            data-testid={`invoice-item-${i}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-[#f59e0b]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[#f59e0b]" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{inv.encf}</p>
                  <p className="text-[#94a3b8] text-xs truncate">{inv.buyer_name} - {inv.type_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right hidden sm:block">
                  <p className="text-white font-bold text-sm">RD$ {inv.total?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}</p>
                  <p className="text-[#94a3b8] text-[10px]">
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString("es-DO", { day: "numeric", month: "short", year: "numeric" }) : ""}
                  </p>
                </div>
                <span className={`text-[10px] px-2 py-1 font-semibold ${st.bg} ${st.text} flex items-center gap-1`}>
                  <StIcon className="w-3 h-3" /> {st.label}
                </span>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// ─── INVOICE DETAIL ───
const InvoiceDetail = ({ invoice, onBack, onCancel }) => {
  const st = STATUS_COLORS[invoice.status] || STATUS_COLORS.draft;
  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-2 text-[#94a3b8] hover:text-white text-sm transition-colors" data-testid="invoice-back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver a facturas
      </button>

      <div className="bg-[#0a0f1a] border border-white/5 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-white text-xl font-bold">{invoice.encf}</h3>
            <p className="text-[#94a3b8] text-sm">{invoice.type_name}</p>
          </div>
          <span className={`text-xs px-3 py-1 font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-[#94a3b8] text-xs mb-1">Emisor</p>
            <p className="text-white text-sm font-medium">{invoice.fiscal_name}</p>
            <p className="text-[#94a3b8] text-xs">RNC: {invoice.fiscal_rnc}</p>
          </div>
          <div>
            <p className="text-[#94a3b8] text-xs mb-1">Comprador</p>
            <p className="text-white text-sm font-medium">{invoice.buyer_name}</p>
            <p className="text-[#94a3b8] text-xs">RNC: {invoice.buyer_rnc}</p>
          </div>
        </div>

        {/* Items */}
        <div className="mb-6">
          <p className="text-[#94a3b8] text-xs mb-2">Detalle</p>
          <div className="border border-white/5">
            <div className="grid grid-cols-12 gap-2 p-3 bg-white/5 text-[#94a3b8] text-xs font-semibold">
              <span className="col-span-5">Descripcion</span>
              <span className="col-span-2 text-right">Cant.</span>
              <span className="col-span-2 text-right">Precio</span>
              <span className="col-span-3 text-right">Total</span>
            </div>
            {invoice.items?.map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 p-3 border-t border-white/5 text-sm">
                <span className="col-span-5 text-white truncate">{item.description}</span>
                <span className="col-span-2 text-right text-[#94a3b8]">{item.quantity}</span>
                <span className="col-span-2 text-right text-[#94a3b8]">${item.unit_price?.toFixed(2)}</span>
                <span className="col-span-3 text-right text-white font-medium">${item.total?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-6">
          <div className="w-full sm:w-64 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#94a3b8]">Subtotal</span>
              <span className="text-white">RD$ {invoice.subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#94a3b8]">ITBIS</span>
              <span className="text-white">RD$ {invoice.tax?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-base font-bold border-t border-white/10 pt-2">
              <span className="text-white">Total</span>
              <span className="text-[#f59e0b]">RD$ {invoice.total?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="grid sm:grid-cols-3 gap-4 p-4 bg-white/5 text-xs">
          <div>
            <span className="text-[#94a3b8]">Forma de Pago:</span>
            <p className="text-white font-medium">{invoice.payment_method_name}</p>
          </div>
          <div>
            <span className="text-[#94a3b8]">Track ID:</span>
            <p className="text-white font-medium">{invoice.track_id}</p>
          </div>
          <div>
            <span className="text-[#94a3b8]">Codigo Seguridad:</span>
            <p className="text-white font-medium">{invoice.security_code}</p>
          </div>
        </div>

        {/* Mock notice */}
        {invoice.dgmax_response?._mock && (
          <div className="mt-4 p-3 bg-[#f59e0b]/5 border border-[#f59e0b]/20 text-[#f59e0b] text-xs">
            Modo simulacion (MOCK) - Conecta credenciales DGMax para facturar en produccion
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          {invoice.status === "registered" && (
            <button
              onClick={() => onCancel(invoice.id)}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 text-red-400 text-sm hover:bg-red-500/20 transition-colors"
              data-testid="cancel-invoice-btn"
            >
              <XCircle className="w-4 h-4" /> Anular Factura
            </button>
          )}
          {invoice.pdf_url && (
            <a
              href={invoice.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-[#94a3b8] text-sm hover:text-white transition-colors"
            >
              <Download className="w-4 h-4" /> PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── CREATE INVOICE FORM ───
const CreateInvoiceForm = ({ fiscalClients, onCreated, onCancel }) => {
  const [form, setForm] = useState({
    fiscal_client_id: "",
    type: "E31",
    buyer_rnc: "",
    buyer_name: "",
    payment_method: "efectivo",
    notes: "",
  });
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0, tax_rate: 0.18 }]);
  const [saving, setSaving] = useState(false);

  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, tax_rate: 0.18 }]);
  const removeItem = (idx) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };
  const updateItem = (idx, field, value) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: field === "description" ? value : parseFloat(value) || 0 };
    setItems(newItems);
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const tax = items.reduce((s, i) => s + i.quantity * i.unit_price * i.tax_rate, 0);
  const total = subtotal + tax;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fiscal_client_id) { toast.error("Selecciona un cliente fiscal emisor"); return; }
    if (!form.buyer_rnc || !form.buyer_name) { toast.error("Completa los datos del comprador"); return; }
    if (items.some((i) => !i.description || i.unit_price <= 0)) { toast.error("Completa todos los items"); return; }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/invoicing/invoices`, { ...form, items });
      toast.success(`Factura ${res.data.encf} creada exitosamente`);
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear factura");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-invoice-form">
      <div className="flex items-center justify-between">
        <h3 className="text-white text-lg font-bold">Nueva Factura Electronica</h3>
        <button type="button" onClick={onCancel} className="text-[#94a3b8] hover:text-white text-sm">Cancelar</button>
      </div>

      <div className="bg-[#0a0f1a] border border-white/5 p-5 space-y-4">
        <p className="text-[#94a3b8] text-xs font-semibold uppercase tracking-wider">Datos del Emisor</p>
        <div>
          <label className="block text-[#94a3b8] text-xs mb-1.5">Empresa Emisora (RNC)</label>
          <select
            value={form.fiscal_client_id}
            onChange={(e) => setForm({ ...form, fiscal_client_id: e.target.value })}
            className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
            data-testid="invoice-fiscal-client"
          >
            <option value="">Seleccionar empresa emisora...</option>
            {fiscalClients.map((fc) => (
              <option key={fc.id} value={fc.id}>{fc.business_name} (RNC: {fc.rnc})</option>
            ))}
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Tipo de Comprobante</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
              data-testid="invoice-type"
            >
              <option value="E31">E31 - Factura de Credito Fiscal</option>
              <option value="E32">E32 - Factura de Consumo</option>
              <option value="E33">E33 - Nota de Debito</option>
              <option value="E34">E34 - Nota de Credito</option>
            </select>
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Forma de Pago</label>
            <select
              value={form.payment_method}
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
              data-testid="invoice-payment"
            >
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia Bancaria</option>
              <option value="tarjeta_credito">Tarjeta de Credito</option>
              <option value="tarjeta_debito">Tarjeta de Debito</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-[#0a0f1a] border border-white/5 p-5 space-y-4">
        <p className="text-[#94a3b8] text-xs font-semibold uppercase tracking-wider">Datos del Comprador</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">RNC/Cedula del Comprador</label>
            <input
              value={form.buyer_rnc}
              onChange={(e) => setForm({ ...form, buyer_rnc: e.target.value })}
              placeholder="Ej: 123456789"
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
              data-testid="invoice-buyer-rnc"
            />
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Nombre/Razon Social</label>
            <input
              value={form.buyer_name}
              onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
              placeholder="Nombre del comprador"
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
              data-testid="invoice-buyer-name"
            />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-[#0a0f1a] border border-white/5 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[#94a3b8] text-xs font-semibold uppercase tracking-wider">Items / Servicios</p>
          <button type="button" onClick={addItem} className="text-[#f59e0b] text-xs font-semibold flex items-center gap-1 hover:text-[#d97706]" data-testid="add-item-btn">
            <Plus className="w-3.5 h-3.5" /> Agregar Item
          </button>
        </div>
        {items.map((item, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-12 sm:col-span-5">
              {i === 0 && <label className="block text-[#94a3b8] text-[10px] mb-1">Descripcion</label>}
              <input
                value={item.description}
                onChange={(e) => updateItem(i, "description", e.target.value)}
                placeholder="Descripcion del item"
                className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
                data-testid={`item-desc-${i}`}
              />
            </div>
            <div className="col-span-4 sm:col-span-2">
              {i === 0 && <label className="block text-[#94a3b8] text-[10px] mb-1">Cantidad</label>}
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={item.quantity}
                onChange={(e) => updateItem(i, "quantity", e.target.value)}
                className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
                data-testid={`item-qty-${i}`}
              />
            </div>
            <div className="col-span-4 sm:col-span-2">
              {i === 0 && <label className="block text-[#94a3b8] text-[10px] mb-1">Precio Unit.</label>}
              <input
                type="number"
                min="0"
                step="0.01"
                value={item.unit_price}
                onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
                data-testid={`item-price-${i}`}
              />
            </div>
            <div className="col-span-3 sm:col-span-2">
              {i === 0 && <label className="block text-[#94a3b8] text-[10px] mb-1">ITBIS %</label>}
              <select
                value={item.tax_rate}
                onChange={(e) => updateItem(i, "tax_rate", e.target.value)}
                className="w-full bg-[#02040a] border border-white/10 px-2 py-2 text-white text-sm focus:border-[#f59e0b] focus:outline-none"
              >
                <option value="0.18">18%</option>
                <option value="0.16">16%</option>
                <option value="0">0%</option>
              </select>
            </div>
            <div className="col-span-1 flex justify-center">
              {items.length > 1 && (
                <button type="button" onClick={() => removeItem(i)} className="text-red-400/60 hover:text-red-400 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Totals Preview */}
        <div className="flex justify-end pt-3 border-t border-white/5">
          <div className="w-full sm:w-56 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[#94a3b8]">Subtotal</span><span className="text-white">RD$ {subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#94a3b8]">ITBIS</span><span className="text-white">RD$ {tax.toFixed(2)}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-white/10 pt-1"><span className="text-white">Total</span><span className="text-[#f59e0b]">RD$ {total.toFixed(2)}</span></div>
          </div>
        </div>
      </div>

      <div className="bg-[#0a0f1a] border border-white/5 p-5">
        <label className="block text-[#94a3b8] text-xs mb-1.5">Notas (opcional)</label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none resize-none"
          data-testid="invoice-notes"
        />
      </div>

      <motion.button
        type="submit"
        disabled={saving}
        className="w-full bg-[#f59e0b] text-black font-bold py-3 text-sm flex items-center justify-center gap-2 hover:bg-[#d97706] transition-colors disabled:opacity-50"
        whileHover={{ boxShadow: "0 0 20px rgba(245,158,11,0.3)" }}
        data-testid="submit-invoice-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
        Emitir Factura Electronica (e-CF)
      </motion.button>
    </form>
  );
};

// ─── MAIN INVOICING TAB ───
const InvoicingTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [fiscalClients, setFiscalClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | create | detail
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [invRes, fcRes, dashRes] = await Promise.all([
        axios.get(`${API}/invoicing/invoices`),
        axios.get(`${API}/invoicing/fiscal-clients`),
        axios.get(`${API}/invoicing/dashboard`),
      ]);
      setInvoices(invRes.data.invoices || []);
      setFiscalClients(fcRes.data.fiscal_clients || []);
      setDashboard(dashRes.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCancel = async (invoiceId) => {
    if (!window.confirm("Seguro que deseas anular esta factura?")) return;
    try {
      await axios.post(`${API}/invoicing/invoices/${invoiceId}/cancel`, {});
      toast.success("Factura anulada");
      fetchData();
      setView("list");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al anular");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoicing-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-xl font-bold">Facturacion Electronica (e-CF)</h2>
          <p className="text-[#94a3b8] text-sm">Emite comprobantes fiscales electronicos DGII</p>
        </div>
        {view === "list" && (
          <button
            onClick={() => setView("create")}
            className="bg-[#f59e0b] text-black text-sm font-bold px-4 py-2 flex items-center gap-2 hover:bg-[#d97706] transition-colors"
            data-testid="new-invoice-btn"
          >
            <Plus className="w-4 h-4" /> Nueva Factura
          </button>
        )}
      </div>

      {/* Dashboard stats */}
      {view === "list" && dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-[#0a0f1a] border border-white/5 p-4">
            <FileText className="w-6 h-6 text-[#f59e0b] mb-2" />
            <p className="text-xl font-bold text-white">{dashboard.total_invoices}</p>
            <p className="text-[#94a3b8] text-xs">Total Facturas</p>
          </div>
          <div className="bg-[#0a0f1a] border border-white/5 p-4">
            <CheckCircle className="w-6 h-6 text-emerald-400 mb-2" />
            <p className="text-xl font-bold text-white">{dashboard.registered}</p>
            <p className="text-[#94a3b8] text-xs">Registradas</p>
          </div>
          <div className="bg-[#0a0f1a] border border-white/5 p-4">
            <XCircle className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-xl font-bold text-white">{dashboard.cancelled}</p>
            <p className="text-[#94a3b8] text-xs">Anuladas</p>
          </div>
          <div className="bg-[#0a0f1a] border border-white/5 p-4">
            <DollarSign className="w-6 h-6 text-[#3b82f6] mb-2" />
            <p className="text-xl font-bold text-white">RD$ {(dashboard.total_amount || 0).toLocaleString("es-DO")}</p>
            <p className="text-[#94a3b8] text-xs">Facturado</p>
          </div>
        </div>
      )}

      {/* No fiscal client notice */}
      {view === "list" && !loading && fiscalClients.length === 0 && (
        <div className="p-4 bg-[#f59e0b]/5 border border-[#f59e0b]/20 text-sm">
          <p className="text-[#f59e0b] font-semibold mb-1">Configura tu empresa primero</p>
          <p className="text-[#94a3b8]">Registra tu RNC en la seccion "Guia e-CF" para poder emitir facturas electronicas.</p>
        </div>
      )}

      <AnimatePresence mode="wait">
        {view === "list" && (
          <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <InvoiceList invoices={invoices} onView={(inv) => { setSelectedInvoice(inv); setView("detail"); }} onCancel={handleCancel} loading={loading} />
          </motion.div>
        )}
        {view === "create" && (
          <motion.div key="create" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
            <CreateInvoiceForm
              fiscalClients={fiscalClients}
              onCreated={() => { fetchData(); setView("list"); }}
              onCancel={() => setView("list")}
            />
          </motion.div>
        )}
        {view === "detail" && selectedInvoice && (
          <motion.div key="detail" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
            <InvoiceDetail
              invoice={selectedInvoice}
              onBack={() => { setView("list"); setSelectedInvoice(null); }}
              onCancel={handleCancel}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvoicingTab;
